/**
 * `Licences` — the GS Multi-Trade licence estate at `/countries/:cc/licences`.
 *
 * ===========================================================================
 * THE FIRST NUMBER ON THIS SCREEN IS THE INTERNAL SPLIT, NOT THE TOTAL
 * ===========================================================================
 * 1,241 of 3,838 imported rows (32.3%) are billed to the group's own Tanzanian
 * arm, and the workbook records no end customer for a single one of them. A
 * register that opened with "3,838 rows" would be answering a question nobody
 * asked with a number nobody has qualified — a third of it is the company
 * invoicing itself. So the split is the band above the table, and it is read from
 * the SERVER's live summary rather than from the design's measured constants: a
 * register imported into a country with nothing in it reads `0 of 0`, which is a
 * real zero and says so.
 *
 * ===========================================================================
 * THE REGISTER IS EMPTY ON EVERY MACHINE UNTIL AN OPERATOR RUNS THE IMPORT
 * ===========================================================================
 * There is no HTTP route that fills this table — importing is a `ts-node` verb
 * (`licences:import -- apply`) whose authorisation is database access. So the
 * empty state is not a transient: on a fresh deployment it is the permanent truth
 * until somebody runs the script, and the word it must never use is "loading".
 * It names the import instead, so the reader knows what is missing and who does
 * it.
 *
 * ===========================================================================
 * WHAT THIS SCREEN CANNOT SHOW, AND WHY THE ABSENCES ARE DELIBERATE
 * ===========================================================================
 * · NO ACTIVATION KEY. None is stored — the importer refuses a run that could
 *   persist one — so no column exists rather than a column of blanks.
 * · NO MONEY. There is none in this estate; the `$126`/`$378` strings are PRODUCT
 *   names. A column headed with a currency would be money invented from a label.
 * · NO OWNER. `latest_customer_name` never travels without `customer_count`
 *   beside it, because a serial resolves to a history: 230 of them have carried
 *   more than one canonical customer, so a lone latest name is confidently wrong
 *   230 times. (231 on the observed canonical names — the basis the design
 *   measured — before the owner's Parth merge collapsed one of them.)
 *
 * ===========================================================================
 * TWO VIEWS, ONE SCREEN — AND THE WORKLIST'S ZEROS ARE PART OF ITS ANSWER
 * ===========================================================================
 * `?view=due-soon` is the renewal call list. Its envelope carries `matched`,
 * `suppressed_count` and `group_entity_count`, and ALL THREE render even at zero:
 * a suppressed row is flagged and hidden and never deleted, so a count that
 * disappeared at nought would make hiding indistinguishable from deleting. The
 * suppression is `(party, product)` and therefore conservative — it can hide a
 * licence whose sibling serial was renewed — and the payload's own disclosure
 * says so on the screen rather than in this comment.
 *
 * Every filter is passed to the server verbatim; nothing is filtered in the
 * client. A page-1 filter applied here would call one page's rows "the estate".
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { PageHeader } from '../../components/shell/AppShell';
import { Alert, Chip, EmptyState, SkeletonRows } from '../../components/ui';
import { cn } from '../../lib/cn';
import {
  fetchLicenceCustomers,
  fetchLicenceRegister,
  fetchLicencesDueSoon,
  licenceErrorText,
  licenceKeys,
} from './api';
import type {
  DueSoonFilters,
  DueSoonPage,
  LicenceClassification,
  LicenceRegisterFilters,
  SuppressedMode,
  TallyLicenceRow,
} from './api';

const PER_PAGE = 50;

/**
 * Grouped with an EXPLICIT locale.
 *
 * `toLocaleString()` with no argument reads the machine's locale, so the same
 * estate would print `1,241` on one operator's screen and `1.241` on another's —
 * and on this screen the two figures are being compared to each other by eye.
 */
const GROUPED = new Intl.NumberFormat('en-US');

function n(value: number): string {
  return GROUPED.format(value);
}

/** `undefined` when the server has not answered yet — never a zero we made up. */
function orDash(value: string | null): string {
  return value === null || value === '' ? 'Not recorded' : value;
}

const CLASSIFICATION_LABEL: Readonly<Record<string, string>> = {
  customer: 'Customer',
  reseller: 'Reseller',
  group_entity: 'Group entity',
  undecided: 'Undecided',
};

/**
 * The classification chip. `undecided` is a REAL state — four customers the owner
 * has not ruled on — so it renders as itself rather than being folded into
 * "Customer", which would decide a question he has not.
 */
function classificationChip(value: LicenceClassification | null): React.JSX.Element | null {
  if (value === null) return null;
  return (
    <Chip
      className={cn(
        value === 'group_entity' && 'bg-info-bg text-info-fg',
        value === 'undecided' && 'bg-warn-bg text-warn-fg',
      )}
    >
      {CLASSIFICATION_LABEL[value] ?? value}
    </Chip>
  );
}

export function LicenceRegisterPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();

  const canView = can(countryCode, 'quotes.view');

  const view = params.get('view') === 'due-soon' ? 'due-soon' : 'register';
  const page = Math.max(1, Number(params.get('page') ?? '1') || 1);
  const serial = params.get('serial');
  const disputed = params.get('disputed') === '1';
  const changedHands = params.get('changed_hands') === '1';
  const suppressed = params.get('suppressed') === 'show' ? 'show' : null;

  const registerFilters = useMemo<LicenceRegisterFilters>(
    () => ({
      serial: serial ?? undefined,
      disputed: disputed ? true : undefined,
      changed_hands: changedHands ? true : undefined,
      page,
      perPage: PER_PAGE,
    }),
    [serial, disputed, changedHands, page],
  );

  const dueFilters = useMemo<DueSoonFilters>(
    () => ({
      suppressed: (suppressed ?? undefined) as SuppressedMode | undefined,
      page,
      perPage: PER_PAGE,
    }),
    [suppressed, page],
  );

  /*
   * THE SPLIT COMES OFF THE CUSTOMERS READ, and one row is fetched with it.
   *
   * `summary` is the only place the internal/external figures exist — they are
   * counts over the whole estate, not over a page — and the customers route is
   * where the server computes them. `perPage: 1` asks for the smallest body that
   * still carries the envelope; asking for none is not an option the route offers,
   * and inventing a fifth endpoint to carry six integers would be a route with one
   * reader.
   */
  const summary = useQuery({
    queryKey: licenceKeys.customers(countryCode, { perPage: 1 }),
    queryFn: ({ signal }) => fetchLicenceCustomers(countryCode, { perPage: 1 }, signal),
    enabled: canView,
  });

  const register = useQuery({
    queryKey: licenceKeys.register(countryCode, registerFilters),
    queryFn: ({ signal }) => fetchLicenceRegister(countryCode, registerFilters, signal),
    enabled: canView && view === 'register',
  });

  const dueSoon = useQuery({
    queryKey: licenceKeys.dueSoon(countryCode, dueFilters),
    queryFn: ({ signal }) => fetchLicencesDueSoon(countryCode, dueFilters, signal),
    enabled: canView && view === 'due-soon',
  });

  if (!canView) {
    /*
     * D5 — the refusal is a designed state naming who to ask. Not a blank screen,
     * and not an empty table, which on THIS screen would read as "this country
     * holds no licences" — a false statement about the data rather than a true one
     * about the account.
     */
    return (
      <div className="flex flex-col gap-4 p-6">
        <Alert tone="danger" title="The licence register is not available to this account">
          Reading the licence register in {countryCode} needs the quotes permission. Ask an
          administrator to widen your access.
        </Alert>
      </div>
    );
  }

  const active = view === 'register' ? register : dueSoon;
  const total = active.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / PER_PAGE));

  const setParam = (key: string, value: string | null, resetPage = true): void => {
    const patch = new URLSearchParams(params);
    if (value === null) patch.delete(key);
    else patch.set(key, value);
    if (resetPage) patch.delete('page');
    setParams(patch, { replace: true });
  };

  const goToPage = (next: number): void => {
    const patch = new URLSearchParams(params);
    if (next <= 1) patch.delete('page');
    else patch.set('page', String(next));
    setParams(patch);
  };

  const disclosures = [
    ...(active.data?.disclosures ?? []),
    ...(summary.data?.disclosures ?? []),
  ].filter((line, index, all) => all.indexOf(line) === index);

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        title="Licences"
        subtitle={
          active.data === undefined
            ? undefined
            : view === 'register'
              ? `${n(total)} ${total === 1 ? 'serial' : 'serials'}`
              : `${n(total)} due in the next ${String(dueSoon.data?.window_days ?? 60)} days`
        }
        actions={
          /*
           * THE WAY IN TO THE REVIEW SURFACE, and it is a LINK rather than a nav row.
           * A second licence destination is the moment this register earns its own
           * sidebar section — a fifth registry in `domain/nav.ts` plus a line in
           * `components/shell/Sidebar.tsx` — and that cost belongs to whoever owns
           * those files. Reached from the register it reviews, which is where its
           * reader already is.
           */
          <Link
            to={`/countries/${countryCode}/licences/disputes`}
            className="text-label font-medium text-indigo underline-offset-2 hover:underline"
          >
            Review flagged rows
          </Link>
        }
      />

      <InternalSplit
        events={summary.data?.summary.events}
        groupEvents={summary.data?.summary.group_entity_events}
        customers={summary.data?.summary.customers}
        groupCustomers={summary.data?.summary.group_entity_customers}
      />

      <div role="group" aria-label="Register view" className="flex flex-wrap items-center gap-2">
        <ViewTab
          label="Register"
          active={view === 'register'}
          onSelect={() => setParam('view', null)}
        />
        <ViewTab
          label="Due soon"
          active={view === 'due-soon'}
          onSelect={() => setParam('view', 'due-soon')}
        />
        {view === 'register' ? (
          <>
            <ViewTab
              label="Disputed only"
              active={disputed}
              onSelect={() => setParam('disputed', disputed ? null : '1')}
            />
            <ViewTab
              label="Changed hands"
              active={changedHands}
              onSelect={() => setParam('changed_hands', changedHands ? null : '1')}
            />
          </>
        ) : null}
      </div>

      {active.isPending ? (
        <div className="rounded-md border border-line bg-surface px-4">
          <SkeletonRows rows={6} label="Loading the licence register" />
        </div>
      ) : active.isError ? (
        <Alert tone="danger" title="The licence register could not be loaded">
          {/* The server's own sentence — on a 400 it names the vocabulary it refused. */}
          <p>{licenceErrorText(active.error)}</p>
          <button
            type="button"
            onClick={() => {
              void active.refetch();
            }}
            className="mt-1 font-medium underline underline-offset-2"
          >
            Try again.
          </button>
        </Alert>
      ) : view === 'register' ? (
        <RegisterTable
          countryCode={countryCode}
          rows={register.data?.data ?? []}
          total={total}
          page={register.data?.page ?? 1}
          onFirstPage={() => goToPage(1)}
        />
      ) : (
        <DueSoonPanel
          countryCode={countryCode}
          data={dueSoon.data}
          onToggleSuppressed={() => setParam('suppressed', suppressed === 'show' ? null : 'show')}
        />
      )}

      {total > PER_PAGE ? (
        <div className="flex items-center justify-end gap-2">
          <span className="text-label text-fg-secondary">
            Page {n(page)} of {n(lastPage)}
          </span>
          <button
            type="button"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            className="inline-flex size-8 items-center justify-center rounded-sm border border-line text-fg disabled:opacity-40"
          >
            <ChevronLeft aria-hidden="true" className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Next page"
            disabled={page >= lastPage}
            onClick={() => goToPage(page + 1)}
            className="inline-flex size-8 items-center justify-center rounded-sm border border-line text-fg disabled:opacity-40"
          >
            <ChevronRight aria-hidden="true" className="size-4" />
          </button>
        </div>
      ) : null}

      {disclosures.length === 0 ? null : (
        <ul data-testid="disclosures" className="flex flex-col gap-1.5">
          {disclosures.map((line) => (
            <li key={line} className="text-label text-fg-secondary">
              {line}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────── the band ── */

/**
 * The split, and it renders BEFORE the server has answered as em-dashes rather
 * than as zeros: an unanswered read and an empty estate are different facts, and
 * printing `0 of 0` while a request is in flight would state the second while the
 * first is true.
 */
function InternalSplit({
  events,
  groupEvents,
  customers,
  groupCustomers,
}: {
  events?: number;
  groupEvents?: number;
  customers?: number;
  groupCustomers?: number;
}): React.JSX.Element {
  const known = events !== undefined && groupEvents !== undefined;
  return (
    <div
      data-testid="internal-split"
      className="flex flex-wrap items-baseline gap-x-6 gap-y-1 rounded-md border border-line bg-surface px-5 py-4"
    >
      <p className="text-body text-fg">
        <span className="tnum text-h3 font-medium">
          {known ? n(groupEvents) : '—'}
        </span>{' '}
        of <span className="tnum font-medium">{known ? n(events) : '—'}</span> licence rows are
        billed to the group’s own arm
      </p>
      <p className="text-label text-fg-secondary">
        {customers === undefined || groupCustomers === undefined
          ? 'External customers: —'
          : `${n(customers - groupCustomers)} of ${n(customers)} customers are external`}
      </p>
    </div>
  );
}

function ViewTab({
  label,
  active,
  onSelect,
}: {
  label: string;
  active: boolean;
  onSelect: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onSelect}
      className={cn(
        'h-8 rounded-pill px-3 text-label font-medium',
        active ? 'bg-indigo-soft text-indigo' : 'bg-subtle text-fg-secondary hover:text-fg',
      )}
    >
      {label}
    </button>
  );
}

/* ─────────────────────────────────────────────────────── the register ── */

function RegisterTable({
  countryCode,
  rows,
  total,
  page,
  onFirstPage,
}: {
  countryCode: string;
  rows: readonly TallyLicenceRow[];
  total: number;
  page: number;
  onFirstPage: () => void;
}): React.JSX.Element {
  if (rows.length === 0 && total > 0) {
    // An empty PAGE is not an empty RESULT SET — the quotes list's V3 lesson.
    return (
      <Alert tone="info" title={`Page ${String(page)} is past the end of this register`}>
        <p>
          The register holds {n(total)} {total === 1 ? 'serial' : 'serials'}.
        </p>
        <button
          type="button"
          onClick={onFirstPage}
          className="mt-1 font-medium underline underline-offset-2"
        >
          Show the first page.
        </button>
      </Alert>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-line bg-surface">
        <EmptyState
          title="The licence register is empty in this country."
          description="It fills when an operator runs the licence import — there is no screen that writes to it. This is a real zero, not a table still loading."
        />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-md border border-line bg-surface">
      <table className="w-full min-w-[820px] text-left text-body">
        <thead className="border-b border-line text-label uppercase text-fg-muted">
          <tr>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Serial
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Latest customer
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Product
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              First event
            </th>
            <th scope="col" className="px-4 py-2.5 font-medium">
              Last event
            </th>
            <th scope="col" className="px-4 py-2.5 text-right font-medium">
              Events
            </th>
            <th scope="col" className="px-4 py-2.5 text-right font-medium">
              Flagged
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="crf-register-row border-b border-line last:border-b-0 align-top">
              <td className="px-4 py-2.5">
                <Link
                  to={`/countries/${countryCode}/licences/by-serial/${row.serial}`}
                  className="font-mono text-indigo underline-offset-2 hover:underline"
                >
                  {row.serial}
                </Link>
              </td>
              <td className="px-4 py-2.5">
                <span className="flex flex-wrap items-center gap-2">
                  <span>{orDash(row.latest_customer_name)}</span>
                  {classificationChip(row.latest_customer_classification)}
                  {/*
                   * THE COUNT TRAVELS WITH THE NAME. A latest name shown alone is
                   * the "owner: X" mistake in table form — and it is wrong 230
                   * times in this estate.
                   */}
                  {row.customer_count > 1 ? (
                    <Chip className="bg-warn-bg text-warn-fg">
                      {n(row.customer_count)} customers
                    </Chip>
                  ) : null}
                </span>
              </td>
              <td className="px-4 py-2.5 text-fg-secondary">{orDash(row.latest_product_name)}</td>
              <td className="tnum px-4 py-2.5 text-fg-secondary">{row.first_event_on}</td>
              <td className="tnum px-4 py-2.5 text-fg-secondary">{row.last_event_on}</td>
              <td className="tnum px-4 py-2.5 text-right text-fg-secondary">
                {n(row.event_count)}
              </td>
              <td className="tnum px-4 py-2.5 text-right">
                {row.disputed_event_count === 0 ? (
                  <span className="text-fg-muted">0</span>
                ) : (
                  <Chip className="bg-warn-bg text-warn-fg">{n(row.disputed_event_count)}</Chip>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─────────────────────────────────────────────────────── the worklist ── */

function DueSoonPanel({
  countryCode,
  data,
  onToggleSuppressed,
}: {
  countryCode: string;
  data: DueSoonPage | undefined;
  onToggleSuppressed: () => void;
}): React.JSX.Element | null {
  if (data === undefined) return null;
  const showing = data.suppressed === 'show';

  return (
    <div className="flex flex-col gap-3">
      <div
        data-testid="due-soon-counts"
        className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-md border border-line bg-surface px-5 py-3 text-label"
      >
        {/*
         * ALL THREE RENDER AT ZERO. `suppressed_count` especially: hiding a row and
         * deleting it must not look the same, and a count that vanished at nought
         * is exactly how they would.
         */}
        <span className="text-fg">
          <span className="tnum font-medium">{n(data.matched)}</span> in the window
        </span>
        <span className="text-fg-secondary">
          <span className="tnum font-medium">{n(data.suppressed_count)}</span> suppressed
        </span>
        <span className="text-fg-secondary">
          <span className="tnum font-medium">{n(data.group_entity_count)}</span> billed to the
          group’s own arm
        </span>
        <span className="text-fg-muted">Estimate: {data.estimate_basis}</span>
        <button
          type="button"
          onClick={onToggleSuppressed}
          className="ml-auto font-medium text-indigo underline underline-offset-2"
        >
          {showing ? 'Hide suppressed' : 'Show suppressed'}
        </button>
      </div>

      {data.data.length === 0 ? (
        <div className="rounded-md border border-dashed border-line bg-surface">
          <EmptyState
            title="No licence in this country is due in this window."
            description="The window is the server’s, not this screen’s — widen it on the route rather than re-counting here."
          />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line bg-surface">
          <table className="w-full min-w-[760px] text-left text-body">
            <thead className="border-b border-line text-label uppercase text-fg-muted">
              <tr>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Serial
                </th>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Customer
                </th>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Product
                </th>
                <th scope="col" className="px-4 py-2.5 font-medium">
                  Due
                </th>
                <th scope="col" className="px-4 py-2.5 text-right font-medium">
                  Days left
                </th>
              </tr>
            </thead>
            <tbody>
              {data.data.map((row) => (
                <tr key={row.licence_id} className="crf-register-row border-b border-line last:border-b-0">
                  <td className="px-4 py-2.5">
                    <Link
                      to={`/countries/${countryCode}/licences/by-serial/${row.serial}`}
                      className="font-mono text-indigo underline-offset-2 hover:underline"
                    >
                      {row.serial}
                    </Link>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="flex flex-wrap items-center gap-2">
                      <span>{orDash(row.customer_name)}</span>
                      {classificationChip(row.classification)}
                      {row.suppressed_by_live_contract ? (
                        <Chip className="bg-info-bg text-info-fg">Suppressed</Chip>
                      ) : null}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-fg-secondary">{orDash(row.product_name)}</td>
                  <td className="tnum px-4 py-2.5">
                    <span className="flex items-center gap-2">
                      {row.due_on}
                      {/* An estimate is labelled an estimate. It is not an expiry date. */}
                      <Chip>{row.due_basis === 'operator' ? 'Operator' : 'Estimated'}</Chip>
                    </span>
                  </td>
                  <td className="tnum px-4 py-2.5 text-right text-fg-secondary">
                    {n(row.days_remaining)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
